import React, { useState, useEffect, useRef } from 'react';

/**
 * Shared Table Component for consistent table behavior across the application
 * Features include:
 * - Checkbox selection
 * - Column resizing 
 * - Column visibility/ordering
 * - Sorting
 * - Custom cell rendering
 * - Pagination
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of data objects to display in the table
 * @param {Array} props.columns - Array of column configuration objects
 * @param {Array} props.selectedItems - Array of selected item IDs
 * @param {Function} props.onSelectItem - Function called when an item is selected/deselected
 * @param {boolean} props.selectAll - Whether all items are selected
 * @param {Function} props.onSelectAll - Function called when select all checkbox is toggled
 * @param {string} props.sortBy - Column ID to sort by
 * @param {string} props.sortOrder - Sort order ('asc' or 'desc')
 * @param {Function} props.onSort - Function called when a sortable column is clicked
 * @param {Function} props.renderCell - Function to render cell content (item, columnId) => JSX
 * @param {boolean} props.loading - Whether the table is loading data
 * @param {string} props.emptyMessage - Message to display when there's no data
 * @param {boolean} props.resizable - Whether columns can be resized
 * @param {Function} props.onColumnsChange - Function called when column config changes
 * @param {boolean} props.showColumnManagement - Whether to show column management UI
 * @param {Function} props.onToggleColumnManagement - Function to toggle column management
 * @param {Object} props.pagination - Pagination object {page, limit, total, pages}
 * @param {Function} props.onPageChange - Function called when page is changed
 * @param {Function} props.onResetColumns - Function called to reset columns to default
 * @param {Function} props.onRowClick - Function called when a row is clicked, receives the item
 * @param {string} props.tableHeight - CSS height value for the table container (e.g. "calc(100vh - 290px)")
 */
const SharedTable = ({
  data = [],
  columns = [],
  selectedItems = [],
  onSelectItem = () => {},
  selectAll = false,
  onSelectAll = () => {},
  sortBy,
  sortOrder,
  onSort = () => {},
  renderCell,
  loading = false,
  emptyMessage = "No items found",
  resizable = false,
  onColumnsChange,
  showColumnManagement = false,
  onToggleColumnManagement = () => {},
  pagination,
  onPageChange,
  onLimitChange, // Add onLimitChange prop
  onResetColumns,
  onRowClick = null,
  tableHeight = "calc(100vh - 290px)"
}) => {
  // State for column management
  const [tableColumns, setTableColumns] = useState(columns);
  
  // For tracking scroll position in column management modal
  const [modalScrollPosition, setModalScrollPosition] = useState(0);
  const [horizontalScrollPosition, setHorizontalScrollPosition] = useState(0);
  const modalListRef = useRef(null);
  const tableContainerRef = useRef(null);
  
  // Update columns when prop changes
  useEffect(() => {
    setTableColumns(columns);
  }, [columns]);
  
  // State for column resizing
  const [resizing, setResizing] = useState(false);
  const [resizeColumnId, setResizeColumnId] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  
  // Restore modal scroll position after columns are reordered
  useEffect(() => {
    if (modalScrollPosition > 0 && showColumnManagement) {
      const modalListElement = document.querySelector('.column-management-list');
      if (modalListElement) {
        // Use RAF to ensure DOM is updated before scrolling
        requestAnimationFrame(() => {
          modalListElement.scrollTop = modalScrollPosition;
        });
      }
    }
  }, [tableColumns, modalScrollPosition, showColumnManagement]);
  
  // Column reordering functions
  const moveColumnUp = (columnId) => {
    // Save the current scroll position before making changes
    const modalListElement = document.querySelector('.column-management-list');
    if (modalListElement) {
      setModalScrollPosition(modalListElement.scrollTop);
    }
    
    const columnIndex = tableColumns.findIndex(col => col.id === columnId);
    if (columnIndex > 0) {
      const newColumns = [...tableColumns];
      // Swap with the previous column
      [newColumns[columnIndex], newColumns[columnIndex - 1]] = 
        [newColumns[columnIndex - 1], newColumns[columnIndex]];
      setTableColumns(newColumns);
      if (onColumnsChange) onColumnsChange(newColumns);
    }
  };
  
  const moveColumnDown = (columnId) => {
    // Save the current scroll position before making changes
    const modalListElement = document.querySelector('.column-management-list');
    if (modalListElement) {
      setModalScrollPosition(modalListElement.scrollTop);
    }
    
    const columnIndex = tableColumns.findIndex(col => col.id === columnId);
    if (columnIndex < tableColumns.length - 1) {
      const newColumns = [...tableColumns];
      // Swap with the next column
      [newColumns[columnIndex], newColumns[columnIndex + 1]] = 
        [newColumns[columnIndex + 1], newColumns[columnIndex]];
      setTableColumns(newColumns);
      if (onColumnsChange) onColumnsChange(newColumns);
    }
  };

  // Column visibility toggle
  const toggleColumnVisibility = (columnId) => {
    // Save the current scroll position before making changes
    const modalListElement = document.querySelector('.column-management-list');
    if (modalListElement) {
      setModalScrollPosition(modalListElement.scrollTop);
    }
    
    const newColumns = tableColumns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    setTableColumns(newColumns);
    if (onColumnsChange) onColumnsChange(newColumns);
  };

  // Get visible columns
  const visibleColumns = tableColumns.filter(col => col.visible);

  // Resize functions
  const handleResizeStart = (e, columnId) => {
    if (!resizable) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Create a global variable to track resize state (avoid React state timing issues)
    window._isResizing = true;
    window._resizeStartX = e.clientX;
    window._resizeColumnId = columnId;
    window._resizeColumnWidth = tableColumns.find(col => col.id === columnId)?.width || 150;
    
    // Set cursor immediately
    document.body.style.cursor = 'col-resize';
    
    // Set React state
    setResizing(true);
    setResizeColumnId(columnId);
    setStartX(e.clientX);
    setStartWidth(window._resizeColumnWidth);
    
    // Add event listeners
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  const handleResizeMove = (e) => {
    if (!window._isResizing) return;
    
    // Calculate how far the mouse has moved from starting position
    const deltaX = e.clientX - window._resizeStartX;
    
    // Apply a sensitivity multiplier to make resizing more responsive
    const sensitivity = 1.5;
    const adjustedDelta = deltaX * sensitivity;
    
    // Minimum width based on column type
    const minWidth = 60;
    
    // Calculate new width based on starting width plus adjusted delta
    const newWidth = Math.max(minWidth, window._resizeColumnWidth + adjustedDelta);
    
    // Create the updated column array
    const updatedColumns = tableColumns.map(col => 
      col.id === window._resizeColumnId 
        ? { ...col, width: newWidth } 
        : col
    );
    
    // Update the state
    setTableColumns(updatedColumns);
    if (onColumnsChange) onColumnsChange(updatedColumns);
  };
  
  const handleResizeEnd = () => {
    // Clear global variables
    window._isResizing = false;
    window._resizeColumnId = null;
    
    // Reset cursor
    document.body.style.cursor = '';
    
    // Reset React state
    setResizing(false);
    setResizeColumnId(null);
    
    // Remove listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };
  
  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);

  // Handle keyboard events for column management modal
  useEffect(() => {
    if (!showColumnManagement) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onToggleColumnManagement(false);
      } else if (e.key === 'Enter') {
        onToggleColumnManagement(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showColumnManagement, onToggleColumnManagement]);
  
  // Column management modal
  const ColumnManagementModal = () => {
    if (!showColumnManagement) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => onToggleColumnManagement(false)}></div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <form onSubmit={(e) => { e.preventDefault(); onToggleColumnManagement(false); }}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Column Management
                    </h3>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Use the arrow buttons to reorder columns. Check or uncheck to show or hide columns.
                        Press ESC to close or Enter to save changes.
                      </p>
                      <ul className="space-y-2 max-h-96 overflow-y-auto column-management-list" ref={modalListRef}>
                        {tableColumns.map((column, index) => (
                          <li 
                            id={`column-${column.id}`}
                            key={column.id} 
                            className="p-2 rounded border flex items-center justify-between border-gray-200 select-none hover:bg-gray-50 transition-colors duration-150"
                          >
                            <div className="flex items-center space-x-3 w-full">
                              {/* Move Up/Down controls */}
                              <div className="flex flex-col">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent form submission
                                    moveColumnUp(column.id);
                                  }}
                                  disabled={index === 0}
                                  className={`p-1 rounded ${
                                    index === 0 
                                      ? 'text-gray-300 cursor-not-allowed' 
                                      : 'text-gray-500 hover:bg-gray-100'
                                  }`}
                                  title="Move up"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent form submission
                                    moveColumnDown(column.id);
                                  }}
                                  disabled={index === tableColumns.length - 1}
                                  className={`p-1 rounded ${
                                    index === tableColumns.length - 1 
                                      ? 'text-gray-300 cursor-not-allowed' 
                                      : 'text-gray-500 hover:bg-gray-100'
                                  }`}
                                  title="Move down"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </div>
                              
                              <label className="checkbox-area flex items-center space-x-2 text-sm text-gray-700 cursor-pointer flex-grow">
                                <input
                                  type="checkbox"
                                  checked={column.visible}
                                  onChange={(e) => {
                                    e.stopPropagation(); // Prevent event bubbling
                                    toggleColumnVisibility(column.id);
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span>{column.label}</span>
                              </label>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Done
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Use onResetColumns if it's provided, or fall back to original behavior
                    if (onResetColumns) {
                      onResetColumns();
                    } else {
                      setTableColumns(columns);
                      if (onColumnsChange) onColumnsChange(columns);
                    }
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Reset to Default
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Table styles for resize functionality
  const tableStyles = {
    position: 'relative'
  };
  
  // Add a resizing overlay when actively resizing
  const resizeOverlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    cursor: 'col-resize',
    userSelect: 'none',
    zIndex: 9999,
    display: resizing ? 'block' : 'none',
    background: 'transparent' // Transparent background but still captures events
  };
  
  // Save horizontal scroll position when sorting
  const handleSort = (columnId) => {
    // Save current horizontal scroll position before sorting
    if (tableContainerRef.current) {
      setHorizontalScrollPosition(tableContainerRef.current.scrollLeft);
    }
    // Call the original onSort function
    onSort(columnId);
  };
  
  // Restore horizontal scroll position after sorting
  useEffect(() => {
    if (horizontalScrollPosition > 0 && tableContainerRef.current) {
      // Use RAF to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        tableContainerRef.current.scrollLeft = horizontalScrollPosition;
      });
    }
  }, [sortBy, sortOrder, horizontalScrollPosition, data]);

  // Render pagination controls
  const renderPagination = () => {
    if (!pagination || !pagination.page || !pagination.limit || !pagination.total) {
      return null;
    }
    
    // Always show pagination info, but only show page controls if we have multiple pages
    const showPageControls = pagination.pages > 1;
    
    // If onPageChange is not a function, provide a fallback
    const handlePageChange = (newPage) => {
      console.log(`Attempting to change to page ${newPage}`);
      if (typeof onPageChange === 'function') {
        onPageChange(newPage);
      } else {
        console.error("onPageChange is not a function", onPageChange);
      }
    };
    
    return (
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              pagination.page === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              pagination.page === pagination.pages 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center space-x-4"> {/* Wrap info and dropdown */}
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-medium">{pagination.total}</span> results
            </p>
            {/* Add Limit Dropdown */}
            <div className="flex items-center">
              <label htmlFor="table-limit" className="text-sm text-gray-700 mr-2">Per page:</label>
              <select
                id="table-limit"
                name="table-limit"
                className="block w-20 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm py-1 px-2"
                value={pagination.limit}
                onChange={(e) => onLimitChange && onLimitChange(e.target.value)}
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
          {showPageControls && (
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    pagination.page === 1 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {[...Array(pagination.pages)].map((_, i) => {
                  const pageNumber = i + 1;
                  // Show first page, last page, current page, and pages around current page
                  if (
                    pageNumber === 1 || 
                    pageNumber === pagination.pages || 
                    (pageNumber >= pagination.page - 1 && pageNumber <= pagination.page + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.page === pageNumber
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  
                  // Show ellipsis for gaps
                  if (
                    (pageNumber === 2 && pagination.page > 3) || 
                    (pageNumber === pagination.pages - 1 && pagination.page < pagination.pages - 2)
                  ) {
                    return (
                      <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    pagination.page === pagination.pages 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col">
      {/* Column Management Modal */}
      <ColumnManagementModal />
      
      {/* Invisible overlay for better mouse tracking during resize */}
      <div style={resizeOverlayStyles}></div>
      
      <div ref={tableContainerRef} className="overflow-auto" style={{ height: tableHeight }}>
        <table className="w-full min-w-full divide-y divide-gray-200 border-collapse" style={tableStyles}>
          <thead className="bg-blue-50 sticky top-0 z-10">
            <tr>
              {/* Checkbox column - always first */}
            <th className="px-2 py-3 w-8 border-r border-gray-200">
              <div className="flex items-center">
                <input
                  id="select-all-checkbox"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={selectAll}
                  onChange={onSelectAll}
                />
                <label htmlFor="select-all-checkbox" className="sr-only">
                  Select all
                </label>
              </div>
            </th>
            
            {/* Dynamic columns based on configuration */}
            {visibleColumns.map((column) => (
              <th 
                key={column.id}
                className={`relative px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 ${
                  column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                }`}
                style={{
                  width: `${column.width}px`, 
                  minWidth: `${column.minWidth || 60}px`,
                  position: 'relative',
                  zIndex: 30 // Make sure columns are above other elements
                }}
                onClick={(e) => {
                  if (column.sortable) {
                    handleSort(column.id);
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {column.label}
                    {column.sortable && (
                      <span className={`ml-1 ${sortBy === column.id ? 'text-blue-600' : 'text-gray-300'}`}>
                        {sortBy === column.id ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                      </span>
                    )}
                  </div>
                  
                  {/* Resize handle with hover visual cue */}
                  {resizable && (
                    <div 
                      className="group"
                      style={{ 
                        position: 'absolute', 
                        right: '-3px',
                        top: 0, 
                        height: '100%', 
                        width: '6px', 
                        cursor: 'col-resize',
                        zIndex: 20
                      }}
                      onMouseDown={(e) => handleResizeStart(e, column.id)}
                      onClick={(e) => e.stopPropagation()} // Prevent sort when clicking resize handle
                    >
                      {/* Visual indicator that appears on hover */}
                      <div 
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                        style={{
                          position: 'absolute',
                          top: '10%',
                          bottom: '10%',
                          left: 'calc(50% - 1px)',
                          width: '2px',
                          backgroundColor: '#3b82f6', /* blue-500 */
                          borderRadius: '1px'
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            <tr>
              <td colSpan={visibleColumns.length + 1} className="px-6 py-8 text-center">
                <div className="flex justify-center">
                  <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={visibleColumns.length + 1} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr 
                key={item.id} 
                className={`group hover:bg-gray-50 ${
                  // Support for row highlighting based on custom attributes
                  selectedItems.includes(item.id) 
                    ? 'bg-blue-50' 
                    : (item.isLastCalled ? 'bg-yellow-50' : '')
                } ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick && onRowClick(item)}
              >
                {/* Checkbox cell - always first */}
                <td className="px-2 py-4 whitespace-nowrap border-r border-gray-200">
                  <div className="flex items-center">
                    <input
                      id={`item-checkbox-${item.id}`}
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => onSelectItem(item.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <label htmlFor={`item-checkbox-${item.id}`} className="sr-only">
                      Select item
                    </label>
                  </div>
                </td>
                
                {/* Dynamic cells based on visible columns */}
                {visibleColumns.map((column) => (
                  <td 
                    key={`${item.id}-${column.id}`} 
                    className="px-6 py-4 whitespace-nowrap overflow-hidden text-ellipsis border-r border-gray-200"
                    style={{ 
                      width: `${column.width}px`, 
                      minWidth: `${column.minWidth || 60}px`, 
                      maxWidth: `${column.width}px`
                    }}
                  >
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                      {renderCell(item, column.id)}
                    </div>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        </table>
      </div>
      
      {/* Pagination controls - outside the scrollable area */}
      <div className="mt-auto">
        {pagination && pagination.total > 0 && renderPagination()}
      </div>
    </div>
  );
};

export default SharedTable;
